import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import SpeedTest from '@cloudflare/speedtest';

import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { getNotification } from '@components/Notification/helpers';

import { getIsLowInternetSpeed } from '@helpers';
import { convertBitToMB } from './helpers';
import { configOptions } from './constants';
import { env } from 'env';
import { useNotificationContext } from '@providers/NotificationProvider';

export const useFastCheckInternetConnectionSpeed = () => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [isShowSpeedTestNotification, setIsShowSpeedTestNotification] =
    useState<boolean | null>(null);
  const isErrorRef = useRef<boolean>(false);
  const speedRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { t: translate } = useTranslation();
  const { showNotification } = useNotification();
  const { activeNotificationId } = useNotificationContext();

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      // very slow connections, connection hanged indefinitely handling case
      // skip if there was an error querying CloudFlare servers
      // otherwise check if current speed is less than required and show notification
      if (
        !isErrorRef.current &&
        convertBitToMB(speedRef.current) <
          +(env.REACT_APP_MIN_SPEED_THRESHOLD_MBPS || 15)
      ) {
        setIsCompleted(true);
      }
    }, +(env.REACT_APP_SPEED_CHECK_TIMEOUT_SECONDS_DEVICE_TEST || 30) * 1000);

    const speedResult = new SpeedTest(configOptions);

    speedResult.onFinish = (results) => {
      const speed = convertBitToMB(results.getDownloadBandwidth() ?? 0);
      speedRef.current = speed;

      if ((speedResult.results as any).raw.download.finished) {
        setIsCompleted(true);
        isErrorRef.current = false;
      }
    };

    speedResult.onResultsChange = () => {
      speedRef.current = convertBitToMB(
        speedResult.results.getDownloadBandwidth() ?? 0,
      );

      // connection to CloudFlare servers has failed/blocked
      if (
        (speedResult.results as any).raw.download.error &&
        !(speedResult.results as any).raw.download.finsihed
      ) {
        const message = `${translate(
          'notifications.unstableInternet',
        )} (Error code: CF)`;

        const notificationData = getNotification({
          message,
          type: NOTIFICATION_TYPES.WARNING,
          closeManually: true,
        });

        const notificationId = showNotification({
          ...notificationData,
          logMessage: message,
        });

        activeNotificationId.current.push(notificationId);

        sendCustomSentryBreadcrumb({ message });
        setIsCompleted(true);
        setIsShowSpeedTestNotification(true);
        isErrorRef.current = true;

        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      }
    };

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCompleted || isErrorRef.current) return;

    const isLowInternetSpeed = getIsLowInternetSpeed(speedRef.current);
    if (!isLowInternetSpeed) {
      setIsShowSpeedTestNotification(false);
      return;
    }

    const message = `Internet connection speed is ${speedRef.current} Mbps`;

    const notificationData = getNotification({
      message: translate('notifications.unstableInternet'),
      type: NOTIFICATION_TYPES.WARNING,
      closeManually: true,
    });

    const notificationId = showNotification({
      ...notificationData,
      logMessage: message,
    });
    activeNotificationId.current.push(notificationId);

    sendCustomSentryBreadcrumb({ message });
    setIsShowSpeedTestNotification(true);
  }, [isCompleted, speedRef.current, translate, showNotification]);

  return {
    speed: speedRef.current,
    isCompleted,
    isShowSpeedTestNotification,
  };
};
